import React, { useState } from 'react';

import { IProductTemplateTableEditor } from 'components/ComponentType';
import ProductTemplateColumnsDropdown from 'components/basic/dropdowns/product-template-columns-dropdown/ProductTemplateColumnsDropdown';
import TimeUnitDropdown from 'components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { InputSwitch, InputSwitchChangeEvent } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { MultiSelectChangeEvent } from 'primereact/multiselect';
import { T } from 'services/lang/LanguageService';

type ProductTemplateConfigProps = {
  currentConfig: IProductTemplateTableEditor;
  setCurrentConfig: React.Dispatch<React.SetStateAction<IProductTemplateTableEditor>>;
};

const ProductTemplateConfig: React.FC<ProductTemplateConfigProps> = ({currentConfig, setCurrentConfig}) => {
  const [overrideConfig, setOverrideConfig] = useState(!!currentConfig.overrideLoadingConfig);

  const handleConfigChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | InputNumberValueChangeEvent
      | InputSwitchChangeEvent
      | MultiSelectChangeEvent
  ) => {
    setCurrentConfig(prevState => ({ ...prevState, [e.target.id]: e.target.value }));
  };

  const loadingConfigHandler = (e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
    setCurrentConfig(prevState => ({
      ...prevState,
      overrideLoadingConfig: {
        ...prevState.overrideLoadingConfig,
        loadingConfig: {
          ...prevState.overrideLoadingConfig?.loadingConfig,
          [e.target.id]: !Number.isNaN(Number(e.target.value))
            ? e.target.value
            : e.target.value.toString().toUpperCase(),
        },
      },
    }));
  };

  const overrideConfigHandler = () => {
    if (overrideConfig) {
      setCurrentConfig(prev => ({ ...prev, overrideLoadingConfig: undefined }));
    }
    setOverrideConfig(prev => !prev);
  };

  return (
    <>
      <div className="w-full flex flex-wrap md:flex-nowrap gap-3 mb-3">
        <div className="w-full md:w-6 gap-2 flex flex-column justify-content-center">
          <label htmlFor="filter">{T("word.hidden-filter")}</label>
          <InputText id="filter" value={currentConfig?.filter ?? ""} onChange={handleConfigChange} />
        </div>
        <div className="w-full md:w-6 gap-2 flex flex-column justify-content-center">
          <label htmlFor="defaultFilter">{T("word.default-filter")}</label>
          <InputText
            id="defaultFilter"
            value={currentConfig?.defaultFilter ?? ""}
            onChange={handleConfigChange}
          />
        </div>
        <div className="w-5 md:w-3 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="itemsPerPage">{T("component.items-per-page")}</label>
          <InputNumber
            id="itemsPerPage"
            value={currentConfig?.itemsPerPage}
            onValueChange={handleConfigChange}
          />
        </div>
        <div className="w-5 md:w-3 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup">
            <InputNumber
              id="updateFrequency"
              value={currentConfig?.updateFrequency as number}
              onValueChange={handleConfigChange}
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
      </div>
      <div className="w-full flex flex-column md:flex-row gap-5 mb-3">
        <div className="flex-1 flex gap-2 justify-content-between">
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="transposed">{T("component.transposed")}</label>
            <InputSwitch
              id="transposed"
              checked={Boolean(currentConfig.transposed)}
              onChange={handleConfigChange}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="showTitle">{T("chart-config.show-title")}</label>
            <InputSwitch
              id="showTitle"
              checked={Boolean(currentConfig.showTitle)}
              onChange={handleConfigChange}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="hideDates">{T("component.hide-dates")}</label>
            <InputSwitch
              id="hideDates"
              checked={Boolean(currentConfig.hideDates)}
              onChange={handleConfigChange}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="showFilter">{T("component.show-filter")}</label>
            <InputSwitch
              id="showFilter"
              checked={Boolean(currentConfig.showFilter)}
              onChange={handleConfigChange}
            />
          </div>
        </div>
        <div className="flex-1 flex gap-2 justify-content-between">
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="allowAddEntry">{T("component.allow-add-entry")}</label>
            <InputSwitch
              id="allowAddEntry"
              checked={Boolean(currentConfig.allowAddEntry)}
              onChange={handleConfigChange}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="allowRowEdit">{T("component.allow-row-edit")}</label>
            <InputSwitch
              id="allowRowEdit"
              checked={Boolean(currentConfig.allowRowEdit)}
              onChange={handleConfigChange}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="showTotals">{T("component.show-totals")}</label>
            <InputSwitch
              id="showTotals"
              checked={Boolean(currentConfig.showTotals)}
              onChange={handleConfigChange}
            />
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="overrideConfig">{T("component.override-config")}</label>
            <InputSwitch
              id="overrideConfig"
              checked={overrideConfig}
              onChange={overrideConfigHandler}
            />
          </div>
        </div>
      </div>
      {currentConfig.safeName && (
        <div className="flex w-full gap-3 mt-3">
          <div className="w-4 md:w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="hiddenColumns">{T("component.hidden-columns")}</label>
            <ProductTemplateColumnsDropdown
              id="hiddenColumns"
              value={Array.isArray(currentConfig.hiddenColumns) ? currentConfig.hiddenColumns : []}
              className="w-full"
              onChange={handleConfigChange}
              selectedTemplate={{
                refName: currentConfig.safeName,
                value: currentConfig.id!
              }}
            />
          </div>
          <div className="w-4 md:w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="onlyShownColumns">{T("component.only-show-columns")}</label>
            <ProductTemplateColumnsDropdown
              id="onlyShownColumns"
              value={
                Array.isArray(currentConfig.onlyShownColumns) ? currentConfig.onlyShownColumns : []
              }
              className="w-full"
              onChange={handleConfigChange}
              selectedTemplate={{
                refName: currentConfig.safeName,
                value: currentConfig.id!
              }}
              disabled={currentConfig.hiddenColumns && currentConfig.hiddenColumns?.length > 0}
            />
          </div>
          <div className="w-4 md:w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="hidePagination">{T("action.hide-pagination")}</label>
            <InputSwitch
              id="hidePagination"
              checked={Boolean(currentConfig.hidePagination)}
              onChange={handleConfigChange}
            />
          </div>
        </div>
      )}
      {overrideConfig && (
        <div className="w-full flex flex-wrap md:flex-nowrap gap-3 mb-3 mt-3">
          <div className="w-8 md:w-6 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="period">{T("load-conf.period")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="period"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.period}
                onValueChange={loadingConfigHandler}
              />
              <TimeUnitDropdown
                id="periodUnit"
                className="p-inputgroup-addon p-0"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.periodUnit || ""}
                onChange={loadingConfigHandler}
              />
            </div>
          </div>
          <div className="w-8 md:w-6 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="offset">{T("load-conf.offset")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="offset"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.offset}
                onValueChange={loadingConfigHandler}
              />
              <TimeUnitDropdown
                id="offsetUnit"
                className="p-inputgroup-addon p-0"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.offsetUnit || ""}
                onChange={loadingConfigHandler}
              />
            </div>
          </div>
          <div className="w-5 md:w-3 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="defaultConfigUnit">{T("load-conf.default-config-unit")}</label>
            <TimeUnitDropdown
              id="defaultConfigUnit"
              className="w-full"
              value={currentConfig.overrideLoadingConfig?.loadingConfig?.defaultConfigUnit || ""}
              onChange={loadingConfigHandler}
            />
          </div>
          <div className="w-5 md:w-3 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="truncateStartUnit">{T("load-conf.truncate-start-unit")}</label>
            <TimeUnitDropdown
              id="truncateStartUnit"
              className="w-full"
              value={currentConfig?.overrideLoadingConfig?.loadingConfig?.truncateStartUnit || ""}
              onChange={loadingConfigHandler}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ProductTemplateConfig